import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import i18n from 'i18n';
import scaleSize from 'utils/scale-size';
import getPCRating from 'utils/get-pc-rating';
import trimMetaItems from 'utils/trim-meta-items';
import getLocalized from 'utils/get-localized';
import { IMDbIcon } from 'components/ContentTitle';
import { getCurrentLangVoliaAPICode } from 'constants/languages';

const UKR_LANG = ['Украинский', 'Ukranian', 'Українська'];

const formatDisplayRunTime = (duration) => {
  if (!duration) return '';
  const splitDuration = duration.split(':');
  const hours = parseInt(splitDuration[0], 10);
  const minutes = parseInt(splitDuration[1], 10);
  const seconds = parseInt(splitDuration[2], 10);
  let humanizeDuration = '';
  if (hours > 0) humanizeDuration += i18n.t('detailsVOD.duration.hours', { count: hours });
  if (minutes > 0) humanizeDuration += i18n.t('detailsVOD.duration.minutes', { count: minutes });
  if (seconds > 0) humanizeDuration += i18n.t('detailsVOD.duration.seconds', { count: seconds });
  return humanizeDuration;
};

export const Metadata = styled.div`
  width: ${scaleSize(1100)}px;
  color: ${({ theme }) => theme.detailsMetadataTextColor};
  font-size: ${scaleSize(34)}px;
  line-height: ${scaleSize(48)}px;
  font-family: 'RobotoMedium', sans-serif;
  position: relative;
  z-index: 3;
  padding-top: ${({ offsetTop = 0 }) => offsetTop}px;
  left: ${scaleSize(220)}px;
  height: ${({ useFixedHeight }) => (useFixedHeight ? `${scaleSize(270)}px` : 'auto')};
`;

export const Title = styled.div`
  width: ${scaleSize(1100)}px;
  color: ${({ theme }) => theme.detailsTitleTextColor};
  font-size: ${({ small }) => scaleSize(small ? 48 : 72)}px;
  line-height: ${({ small }) => scaleSize(small ? 58 : 88)}px;
  font-family: 'RobotoBold', sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FirstLine = styled.div`
  color: ${({ theme }) => theme.detailsTitleTextColor};
  margin-top: ${({ small }) => scaleSize(small ? 33 : 54)}px;
  margin-bottom: ${scaleSize(17)}px;
  font-size: ${scaleSize(32)}px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;

  span {
    padding-right: ${scaleSize(30)}px;
  }
`;

export const Line = styled.div`
  display: flex;
  line-height: ${scaleSize(38)}px;
  font-size: ${scaleSize(24)}px;
  margin-top: ${scaleSize(22)}px;
  min-height: ${scaleSize(38)}px;

  div:first-child {
    width: ${scaleSize(250)}px;
    flex-shrink: 0;
    color: ${({ theme }) => theme.detailsMetadataTextColor};
  }

  div {
    color: ${({ theme }) => theme.detailsTitleTextColor};
  }
`;

const DetailsMetadata = ({ data, offset, small, isVisible }) => {
  const { title, imdbRating, pgRating = {}, genres } = data;
  const runTime = useMemo(() => {
    return formatDisplayRunTime(title?.displayRunTime || data.seasons?.[0].episodes?.content[0].title.displayRunTime);
  }, [title, data]);
  const year = title?.year || data.year;
  const rating = getPCRating(pgRating.name || pgRating.UA)?.age;

  const genresToShow = trimMetaItems(genres, 3);
  const countriesToShow = trimMetaItems(data.countriesOfOrigin, 3);
  const tracks = [...data.originalLanguages, ...data.dubbedLanguages]
    .map((lang) => {
      const key = getCurrentLangVoliaAPICode();
      return lang[key];
    })
    // unique values only
    .filter((v, i, arr) => arr.indexOf(v) === i)
    // ua as first lang
    .sort((a, b) => {
      if (UKR_LANG.includes(a)) return -1;
      if (UKR_LANG.includes(b)) return 1;
      return 0;
    })
    .join(', ');

  return (
    <Metadata offsetTop={offset} useFixedHeight={!isVisible}>
      <Title small={small}>{getLocalized(data.titles, data.titles?.UA?.title || data.title)}</Title>
      <FirstLine>
        {runTime && <span>{runTime}</span>}
        {i18n.t('detailsVOD.year', { year })}
        <span>
          {!!imdbRating && (
            <>
              <IMDbIcon /> {imdbRating}
            </>
          )}
        </span>
        <span>{rating}</span>
      </FirstLine>
      <Line>
        {genresToShow && <div>{i18n.t('detailsVOD.genre')}</div>}
        <div>{genresToShow}</div>
      </Line>
      <Line>
        {countriesToShow && <div>{i18n.t('detailsVOD.country')}</div>}
        <div>{countriesToShow}</div>
      </Line>
      {tracks && (
        <Line>
          <div>{i18n.t('detailsVOD.audio')}</div>
          <div>{tracks}</div>
        </Line>
      )}
    </Metadata>
  );
};

export default DetailsMetadata;
